import { Box, Tooltip, Typography } from '@mui/material';
import Button from 'domains/core/components/Button';
import { SMSAutoResponse } from 'domains/content/types';
import AutoResponse from './AutoResponse';
import ReservedKeywords from './ReservedKeywords';
import { useCallback, useEffect } from 'react';

const MAX_AUTO_RESPONSE_COUNT = 5;

type Props = {
    autoResponses?: SMSAutoResponse[];
    setAutoResponses: (autoResponses: SMSAutoResponse[]) => void;
    setHasInvalidAutoResponses: (hasInvalidAutoResponses: boolean) => void;
};

const AutoResponses = ({ autoResponses, setAutoResponses, setHasInvalidAutoResponses }: Props) => {
    const getIsValidAutoResponse = useCallback((autoResponse: SMSAutoResponse) => {
        const { keywords, text } = autoResponse;
        if (!!keywords.length && !text) return false;
        if (!!text && !keywords.length) return false;
        return true;
    }, []);
    const hasValidAutoResponses = autoResponses.every(getIsValidAutoResponse);

    useEffect(() => {
        if (hasValidAutoResponses) {
            setHasInvalidAutoResponses(false);
        } else {
            setHasInvalidAutoResponses(true);
        }
    }, [hasValidAutoResponses, setHasInvalidAutoResponses]);

    const addAutoResponse = () => {
        const updatedAutoResponses = [...autoResponses, { keywords: [], text: '' }];
        setAutoResponses(updatedAutoResponses);
    };

    const renderAutoResponses = (autoResponse: SMSAutoResponse, index: number) => {
        const removeAutoResponse = () => {
            const updatedAutoResponses = [...autoResponses];
            updatedAutoResponses.splice(index, 1);
            setAutoResponses(updatedAutoResponses);
        };

        const updateAutoResponse = (autoResponse: SMSAutoResponse) => {
            const updatedAutoResponses = [...autoResponses];
            updatedAutoResponses[index] = autoResponse;
            setAutoResponses(updatedAutoResponses);
        };

        const keywordsUsed = autoResponses?.flatMap((autoResponse, indexOfAutoResponse) => {
            if (indexOfAutoResponse === index) {
                return [];
            } else {
                return autoResponse?.keywords;
            }
        });

        return (
            <AutoResponse
                key={`${autoResponse?.keywords[0]}-${index}`}
                autoResponse={autoResponse}
                keywordsUsed={keywordsUsed}
                setHasInvalidAutoResponses={setHasInvalidAutoResponses}
                removeAutoResponse={removeAutoResponse}
                updateAutoResponse={updateAutoResponse}
            />
        );
    };

    const isDisabled = autoResponses?.length === MAX_AUTO_RESPONSE_COUNT;

    return (
        <Box mt={4}>
            <Typography variant="h6" mb={1}>
                Auto-Responses
            </Typography>
            <Typography variant="body1">Set up text message replies based on keywords.</Typography>
            <ReservedKeywords />
            {autoResponses?.map((autoResponse: SMSAutoResponse, index: number) =>
                renderAutoResponses(autoResponse, index)
            )}
            <Tooltip
                title={isDisabled && `You can add up to ${MAX_AUTO_RESPONSE_COUNT} auto-responses.`}
                sx={{ display: 'inline-block' }}
            >
                <Box>
                    <Button disabled={isDisabled} variant="contained" onClick={addAutoResponse} sx={{ marginTop: 3 }}>
                        Add an Auto-Response
                    </Button>
                </Box>
            </Tooltip>
        </Box>
    );
};

export default AutoResponses;
